import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLButtonElement} [desktopShowMoreButton] - Desktop show more/less button.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  static STICKY_HEADER_OFFSET = 78;
  static VIDEO_AUTOPLAY_THRESHOLD = 0.5;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupVideoAutoplayOnScroll();
  }

  #controller = new AbortController();
  #videoObserver = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#videoObserver?.disconnect();
    this.#videoObserver = null;
    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialog = this.refs.zoomDialogComponent;
    if (typeof zoomDialog?.loadHighResolutionImage !== 'function') return;

    const mediaContainer =
        zoomDialog.refs?.media?.[index] ?? this.refs.media?.[index];
    if (!mediaContainer) return;

    zoomDialog.loadHighResolutionImage(mediaContainer);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  /** Toggles desktop grid expansion state. */
  toggleDesktopGridExpansion() {
    const wasExpanded = this.classList.contains('is-desktop-expanded');

    this.classList.toggle('is-desktop-expanded');
    this.#syncDesktopToggleButton();

    if (wasExpanded) {
      requestAnimationFrame(() => {
        const top = Math.max(
          0,
          window.scrollY + this.getBoundingClientRect().top - MediaGallery.STICKY_HEADER_OFFSET
        );
        window.scrollTo({ top, behavior: 'smooth' });
      });
    }
  }

  /** Enables autoplay/pause behavior for in-view deferred product videos. */
  #setupVideoAutoplayOnScroll() {
    if (this.dataset.autoplayVideoOnScroll !== 'true') return;

    const deferredVideos = this.querySelectorAll('.product-media-container--video deferred-media');
    if (deferredVideos.length === 0) return;

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const media = entry.target;
          if (!(media instanceof HTMLElement)) return;

          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= MediaGallery.VIDEO_AUTOPLAY_THRESHOLD
          ) {
            this.#playDeferredVideo(media);
            this.#videoObserver?.unobserve(media);
          }
        });
      },
      { threshold: [0, MediaGallery.VIDEO_AUTOPLAY_THRESHOLD] }
    );

    deferredVideos.forEach((video) => this.#videoObserver?.observe(video));
  }

  /**
   * Loads deferred video content (if needed) and starts playback.
   * @param {HTMLElement} media
   */
  #playDeferredVideo(media) {
    media.loadContent(false);

    setTimeout(() => {
      media.playMedia();
    }, 10);
  }

  #syncDesktopToggleButton() {
    const isExpanded = this.classList.contains('is-desktop-expanded');
    const toggleButton = this.refs.desktopShowMoreButton;
    const label = isExpanded ? toggleButton.dataset.labelLess : toggleButton.dataset.labelMore;

    toggleButton.setAttribute('aria-expanded', String(isExpanded));
    toggleButton.textContent = label;
    toggleButton.setAttribute('aria-label', label);
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
